import type { JsonValue } from "@e308/core";
import {
  type CombatReport,
  type CombatState,
  INFRASTRUCTURE_TARGETS,
  type InfrastructureTarget,
} from "./combat-types.js";
import { formatMicro, microFromString } from "./exact-numbers.js";
import { troopQuantity } from "./logistics-math.js";
import { exactRecordKeys as exactKeys, recordObject } from "./record-validation.js";

export function initialCombatState(borderIds: readonly string[]): CombatState & JsonValue {
  return cloneCombatState({
    borders: Object.fromEntries(
      borderIds.map((id) => [
        id,
        {
          shieldCharge: "0",
          requestedTarget: "headquarters",
          effectiveTarget: "headquarters",
          lastAttack: null,
        },
      ]),
    ),
  }) as CombatState & JsonValue;
}

export function setInfrastructureTarget(
  state: CombatState,
  borderId: string,
  target: InfrastructureTarget,
): CombatState & JsonValue {
  const border = state.borders[borderId];
  if (!border) throw new TypeError(`Unknown border: ${borderId}`);
  return cloneCombatState({
    borders: { ...state.borders, [borderId]: { ...border, requestedTarget: target } },
  }) as CombatState & JsonValue;
}

export function settleShields(
  state: CombatState,
  totalCapacity: string,
  totalRecharge: string,
  allocations: Readonly<Record<string, number>>,
): CombatState & JsonValue {
  const capacity = microFromString(troopQuantity(totalCapacity, "shield capacity"));
  const recharge = microFromString(troopQuantity(totalRecharge, "shield recharge"));
  exactKeys(allocations, Object.keys(state.borders));
  if (Object.values(allocations).reduce((sum, value) => sum + value, 0) > 100)
    throw new TypeError("Shield allocation cannot exceed 100");
  return cloneCombatState({
    borders: Object.fromEntries(
      Object.entries(state.borders).map(([id, border]) => {
        const percent = allocations[id];
        if (!Number.isInteger(percent) || (percent as number) < 0 || (percent as number) > 100)
          throw new TypeError("Shield allocation must be a whole percentage from 0 to 100");
        const maximum = (capacity * BigInt(percent as number)) / 100n;
        const added = (recharge * BigInt(percent as number)) / 100n;
        const clamped = minimum(microFromString(border.shieldCharge), maximum);
        return [
          id,
          { ...border, shieldCharge: formatMicro(minimum(maximum, clamped + added) as never) },
        ];
      }),
    ),
  }) as CombatState & JsonValue;
}

export function validateCombatState(
  value: unknown,
  borderIds: readonly string[],
): CombatState & JsonValue {
  const source = recordObject(value, "combat state");
  exactKeys(source, ["borders"]);
  const borders = recordObject(source.borders, "combat borders");
  exactKeys(borders, borderIds);
  return cloneCombatState({
    borders: Object.fromEntries(
      borderIds.map((id) => {
        const border = recordObject(borders[id], `combat border ${id}`);
        exactKeys(border, ["shieldCharge", "requestedTarget", "effectiveTarget", "lastAttack"]);
        return [
          id,
          {
            shieldCharge: troopQuantity(border.shieldCharge, "shield charge"),
            requestedTarget: target(border.requestedTarget),
            effectiveTarget: target(border.effectiveTarget),
            lastAttack: border.lastAttack === null ? null : report(border.lastAttack),
          },
        ];
      }),
    ),
  }) as CombatState & JsonValue;
}

export function cloneCombatState(state: CombatState): CombatState {
  return Object.freeze({
    borders: Object.freeze(
      Object.fromEntries(
        Object.entries(state.borders).map(([id, border]) => [
          id,
          Object.freeze({
            ...border,
            lastAttack: border.lastAttack ? Object.freeze({ ...border.lastAttack }) : null,
          }),
        ]),
      ),
    ),
  });
}

function report(value: unknown): CombatReport {
  const source = recordObject(value, "combat report");
  exactKeys(source, [
    "effectiveTarget",
    "shieldAbsorbed",
    "troopDamage",
    "infrastructureDamage",
    "blastDoorsActive",
  ]);
  if (typeof source.blastDoorsActive !== "boolean") throw new TypeError("Invalid Blast Doors flag");
  return {
    effectiveTarget: target(source.effectiveTarget),
    shieldAbsorbed: troopQuantity(source.shieldAbsorbed, "absorbed shield damage"),
    troopDamage: troopQuantity(source.troopDamage, "troop damage"),
    infrastructureDamage: troopQuantity(source.infrastructureDamage, "infrastructure damage"),
    blastDoorsActive: source.blastDoorsActive,
  };
}

function target(value: unknown): InfrastructureTarget {
  if (!INFRASTRUCTURE_TARGETS.includes(value as InfrastructureTarget))
    throw new TypeError("Invalid infrastructure target");
  return value as InfrastructureTarget;
}

function minimum(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}
